import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, Checkbox, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  deleteShoppingItem,
  getShoppingItems,
  shoppingStatuses,
} from '@/features/shopping/api/shopping';

export function ShoppingListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const items = useQuery({ queryKey: ['shopping'], queryFn: getShoppingItems });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['shopping'] });
  const remove = useMutation({
    mutationFn: deleteShoppingItem,
    onSuccess: async () => {
      setSelectedId(null);
      await refresh();
    },
  });
  const rows = (items.data ?? []).filter((item) => filter === null || item.status === filter);
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5, px: 1, py: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} minHeight={44}>
          {selectedId === null ? (
            <Button
              sx={commandButtonSx}
              startIcon={<AddIcon />}
              onClick={() => void navigate('/shopping-list/new')}
            >
              New Item
            </Button>
          ) : (
            <>
              <Button
                sx={commandButtonSx}
                startIcon={<EditOutlinedIcon />}
                onClick={() => void navigate(`/shopping-list/${selectedId}`)}
              >
                Edit
              </Button>
              <Button
                sx={deleteButtonSx}
                startIcon={<DeleteOutlineIcon />}
                disabled={remove.isPending}
                onClick={() => remove.mutate(selectedId)}
              >
                Delete
              </Button>
            </>
          )}
          <Button sx={commandButtonSx} startIcon={<RefreshIcon />} onClick={() => void refresh()}>
            Refresh
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Typography variant="h1">Shopping List</Typography>
          <Typography color="text.secondary">
            Track requested supplies from request through installation.
          </Typography>
          <Tabs
            value={filter === null ? 'all' : filter}
            onChange={(_, value: string | number) => {
              setFilter(value === 'all' ? null : Number(value));
              setSelectedId(null);
            }}
            variant="scrollable"
            sx={{ mt: 1 }}
          >
            <Tab value="all" label="All Items" />
            {shoppingStatuses.map((status, index) => (
              <Tab key={status} value={index} label={status} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ p: 1.5, overflowX: 'auto' }}>
          {items.isError && <Alert severity="error">Shopping items could not be loaded.</Alert>}
          {remove.isError && (
            <Alert severity="error">The selected item could not be deleted.</Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '52px minmax(220px,1.2fr) minmax(180px,1fr) 150px',
              minWidth: 760,
            }}
          >
            <Box sx={cellSx} />
            {['Description / Location', 'Pool', 'Status'].map((label) => (
              <Typography key={label} fontWeight={600} sx={cellSx}>
                {label}
              </Typography>
            ))}
            {rows.map((item) => {
              const selected = selectedId === item.id;
              const select = () => setSelectedId(selected ? null : item.id);
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'contents',
                    '& > *': { backgroundColor: selected ? '#eaf3fc' : 'transparent' },
                  }}
                >
                  <Box sx={cellSx}>
                    <Checkbox
                      inputProps={{ 'aria-label': `Select ${item.description}` }}
                      checked={selected}
                      onChange={select}
                    />
                  </Box>
                  <Box sx={{ ...cellSx, cursor: 'pointer' }} onClick={select}>
                    <Typography>{item.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.clientName} · {item.locationName}
                    </Typography>
                  </Box>
                  <Typography sx={{ ...cellSx, cursor: 'pointer' }} onClick={select}>
                    {item.bodyName}
                  </Typography>
                  <Typography sx={{ ...cellSx, cursor: 'pointer' }} onClick={select}>
                    {shoppingStatuses[item.status]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {!items.isLoading && rows.length === 0 && (
            <Typography color="text.secondary" textAlign="center" sx={{ py: 5 }}>
              We didn't find anything to show here.
            </Typography>
          )}
          <Typography variant="body2" sx={{ pt: 1.5 }}>
            Rows: {rows.length}
            {selectedId !== null ? '   Selected: 1' : ''}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const cellSx = {
  minHeight: 52,
  px: 1.5,
  py: 1.1,
  borderBottom: '1px solid #edebe9',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteButtonSx = { color: 'text.primary', '& .MuiButton-startIcon': { color: 'error.main' } };
